import { useEffect, useMemo, useState } from "react";
import { Modal } from "./Modal";
import { api, type Tenant, type TenantTheme } from "../api";

/** Parse a hex string like "#2369d2" or "#abc" into RGB channels. Returns
 *  null for anything that isn't a valid 3- or 6-digit hex — we treat that
 *  as "no value" so the contrast panel just hides. */
function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const s = hex.trim().toLowerCase();
  const m6 = s.match(/^#?([0-9a-f]{6})$/);
  const m3 = s.match(/^#?([0-9a-f]{3})$/);
  const raw = m6?.[1] ?? (m3 ? m3[1].split("").map((c) => c + c).join("") : null);
  if (!raw) return null;
  return { r: parseInt(raw.slice(0, 2), 16), g: parseInt(raw.slice(2, 4), 16), b: parseInt(raw.slice(4, 6), 16) };
}

function luminance(r: number, g: number, b: number): number {
  const ch = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

function contrast(fg: { r: number; g: number; b: number }, bg: { r: number; g: number; b: number }): number {
  const [hi, lo] = luminance(fg.r, fg.g, fg.b) > luminance(bg.r, bg.g, bg.b)
    ? [luminance(fg.r, fg.g, fg.b), luminance(bg.r, bg.g, bg.b)]
    : [luminance(bg.r, bg.g, bg.b), luminance(fg.r, fg.g, fg.b)];
  return (hi + 0.05) / (lo + 0.05);
}

const WHITE = { r: 255, g: 255, b: 255 };

/** SmartComply defaults — shown as the "leave blank to inherit" fallback. */
const DEFAULTS: Required<{ primary: string; primaryDeep: string; tint: string; accent: string }> = {
  primary: "#2369d2",
  primaryDeep: "#144296",
  tint: "#e8eff8",
  accent: "#f5f572",
};

interface Props {
  tenant: Tenant | null;
  onClose: () => void;
  onSaved: (t: Tenant) => void;
}

/** Editor for `Tenant.theme` — brand name/suffix/initials/logo + four
 *  color knobs. Live-computes the primary's contrast against white so
 *  admins get a warning before shipping an inaccessible palette. */
export function TenantThemeModal({ tenant, onClose, onSaved }: Props) {
  const [brandName, setBrandName] = useState("");
  const [brandSuffix, setBrandSuffix] = useState("");
  const [brandInitials, setBrandInitials] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primary, setPrimary] = useState("");
  const [primaryDeep, setPrimaryDeep] = useState("");
  const [tint, setTint] = useState("");
  const [accent, setAccent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant) return;
    const t = tenant.theme || {};
    const c = t.colors || {};
    setBrandName(t.brand_name ?? "");
    setBrandSuffix(t.brand_suffix ?? "");
    setBrandInitials(t.brand_initials ?? "");
    setLogoUrl(t.logo_url ?? "");
    setPrimary(c.primary ?? "");
    setPrimaryDeep(c.primary_deep ?? "");
    setTint(c.tint ?? "");
    setAccent(c.accent ?? "");
    setErr(null);
  }, [tenant?.id]);

  const primaryRgb = useMemo(() => parseHex(primary || DEFAULTS.primary), [primary]);
  const contrastWhite = primaryRgb ? contrast(primaryRgb, WHITE) : null;
  // White text on a primary button needs 4.5:1 for normal text (WCAG AA);
  // 3.0:1 for the large text used inside big primary buttons in the Reader
  // is also acceptable, but we anchor on the stricter number.
  const contrastLabel = contrastWhite == null ? null : (
    contrastWhite >= 4.5 ? { text: `Passes AA (${contrastWhite.toFixed(2)}:1)`, tone: "ok" as const } :
    contrastWhite >= 3.0 ? { text: `Marginal (${contrastWhite.toFixed(2)}:1) — OK for large text only`, tone: "warn" as const } :
                           { text: `Fails AA (${contrastWhite.toFixed(2)}:1) — white button text will be hard to read`, tone: "bad" as const }
  );

  async function save() {
    if (!tenant) return;
    setBusy(true);
    setErr(null);
    try {
      const theme: TenantTheme = {
        brand_name: brandName.trim() || null,
        brand_suffix: brandSuffix.trim() || null,
        brand_initials: brandInitials.trim().slice(0, 3) || null,
        logo_url: logoUrl.trim() || null,
        colors: {
          primary: primary.trim() || null,
          primary_deep: primaryDeep.trim() || null,
          tint: tint.trim() || null,
          accent: accent.trim() || null,
        },
      };
      const updated = await api.setTenantTheme(tenant.id, theme);
      onSaved(updated);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function resetToDefaults() {
    setBrandName("");
    setBrandSuffix("");
    setBrandInitials("");
    setLogoUrl("");
    setPrimary("");
    setPrimaryDeep("");
    setTint("");
    setAccent("");
  }

  const previewPrimary = primary || DEFAULTS.primary;
  const previewDeep = primaryDeep || DEFAULTS.primaryDeep;
  const previewTint = tint || DEFAULTS.tint;
  const previewInitials = (brandInitials || (brandName || "SmartComply").slice(0, 2)).toUpperCase();

  return (
    <Modal
      open={!!tenant}
      onClose={onClose}
      title={`Reader theme — ${tenant?.name ?? ""}`}
      size="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={resetToDefaults}
            disabled={busy}
          >
            Reset to SmartComply defaults
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save theme"}
            </button>
          </div>
        </div>
      }
    >
      <div className="grid gap-5">
        {/* Live preview — the Reader's wordmark and primary button rendered
            with the current values so admins see the effect before saving. */}
        <div
          className="rounded-lg border border-slate-200 p-4"
          style={{ background: previewTint }}
        >
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Preview</div>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[11px] font-bold overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${previewPrimary}, ${previewDeep})` }}
              aria-hidden="true"
            >
              {logoUrl.trim()
                ? <img src={logoUrl.trim()} alt="" className="w-full h-full object-contain" />
                : previewInitials}
            </div>
            <div className="text-[15px] font-semibold text-slate-900 truncate">
              {brandName.trim() || "SmartComply"}
              <span className="text-slate-500 font-normal text-xs ml-1">{brandSuffix.trim() || "by SmartRoots"}</span>
            </div>
            <button
              type="button"
              className="ml-auto rounded-full text-white text-xs font-semibold px-4 py-1.5"
              style={{ background: previewPrimary }}
              tabIndex={-1}
            >
              Ask
            </button>
          </div>
          {contrastLabel && (
            <p className={
              "text-xs mt-3 " +
              (contrastLabel.tone === "ok" ? "text-emerald-700" :
               contrastLabel.tone === "warn" ? "text-amber-700" : "text-red-700")
            }>
              White-text on primary: {contrastLabel.text}
            </p>
          )}
        </div>

        <fieldset className="grid gap-3">
          <legend className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Wordmark</legend>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-slate-700 mb-1">Brand name</span>
              <input
                className="input w-full"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="SmartComply"
                maxLength={60}
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-700 mb-1">Suffix (small)</span>
              <input
                className="input w-full"
                value={brandSuffix}
                onChange={(e) => setBrandSuffix(e.target.value)}
                placeholder="by SmartRoots"
                maxLength={60}
              />
            </label>
            <label className="text-sm">
              <span className="block text-slate-700 mb-1">Fallback initials</span>
              <input
                className="input w-full"
                value={brandInitials}
                onChange={(e) => setBrandInitials(e.target.value.toUpperCase())}
                placeholder="SC"
                maxLength={3}
              />
              <span className="block text-xs text-slate-500 mt-1">Shown when no logo URL is set. 2–3 characters.</span>
            </label>
            <label className="text-sm">
              <span className="block text-slate-700 mb-1">Logo URL</span>
              <input
                className="input w-full"
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://…/logo.svg"
              />
              <span className="block text-xs text-slate-500 mt-1">SVG or PNG. Displays inside a 28×28 rounded square.</span>
            </label>
          </div>
        </fieldset>

        <fieldset className="grid gap-3">
          <legend className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Colors</legend>
          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Primary" hint="Buttons + active state" value={primary} onChange={setPrimary} placeholder={DEFAULTS.primary} />
            <ColorField label="Primary (deep)" hint="Wordmark gradient + text on tinted panels" value={primaryDeep} onChange={setPrimaryDeep} placeholder={DEFAULTS.primaryDeep} />
            <ColorField label="Tint" hint="Active-panel background" value={tint} onChange={setTint} placeholder={DEFAULTS.tint} />
            <ColorField label="Accent" hint="Current-page pip (used sparingly)" value={accent} onChange={setAccent} placeholder={DEFAULTS.accent} />
          </div>
          <p className="text-xs text-slate-500">Leave any field blank to inherit the SmartComply default. Document content colours are never affected — only the reader chrome.</p>
        </fieldset>

        {err && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            Could not save: {err}
          </p>
        )}
      </div>
    </Modal>
  );
}

function ColorField({ label, hint, value, onChange, placeholder }: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const valid = value.trim() === "" || parseHex(value) !== null;
  return (
    <label className="text-sm">
      <span className="block text-slate-700 mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={parseHex(value)
            ? (value.startsWith("#") ? value : "#" + value)
            : placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded border border-slate-200 shrink-0 cursor-pointer p-0"
          aria-label={`${label} colour picker`}
        />
        <input
          className="input w-full font-mono text-xs"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={9}
        />
      </div>
      <span className="block text-xs text-slate-500 mt-1">
        {hint}
        {!valid && <span className="text-red-600"> · Not a valid hex.</span>}
      </span>
    </label>
  );
}
